<script lang="ts">
    import {LoadingPage} from 'Components/UI';
    import translations from 'Assets/i18n';
    import {getContext, hasContext, onDestroy, onMount, setContext} from 'svelte';
    import PresentationContext from 'Pages/PresentationPage/Lib/PresentationContext';
    import DependencyContainer from 'Lib/DependencyContainer';
    import UserContext from 'Lib/UserContext';
    import PresentationBootstrapService from 'PresentationBootstrap/Lib/Services/PresentationBootstrapService';
    import UserActivityService from 'PresentationBootstrap/Lib/Services/UserActivityService';
    import {applicationContextKey} from 'PublicationDependencies';
    import ErrorPage from 'Components/ErrorPage.svelte';
    import PasswordPromptDialog from 'Pages/PublicationPage/Components/PasswordPromptDialog.svelte';
    import type {Nullable} from 'Lib/Utils/Nullable';
    import EmbeddedPresentationStarter from 'PresentationBootstrap/Lib/EmbeddedPresentationStarter.svelte';
    import URLParamsUtils from 'PresentationBootstrap/Lib/URLParamsUtils';
    import CornerBanner from 'Components/CornerBanner.svelte';
    import * as Style from 'Components/Style';
    import PresentationLogViewer from 'PresentationBootstrap/Lib/PresentationLogViewer.svelte';

    let applicationContext: DependencyContainer;

    const isEmbedded = !hasContext(applicationContextKey);

    let userActivityService: UserActivityService | undefined;
    let autostart = !isEmbedded;
    if (isEmbedded) {
        // Embedded mode
        console.info('Presentation bootstrap running in embedded / standalone mode.');

        applicationContext = new DependencyContainer();
        setContext(applicationContextKey, applicationContext);
        onDestroy(() => applicationContext.onDestroy());

        // Contexts
        applicationContext.registerSingleton(UserContext);
        applicationContext.registerSingleton(PresentationContext);

        // Services
        applicationContext.registerSingleton(UserActivityService);
        applicationContext.registerSingleton(PresentationBootstrapService);

        userActivityService = applicationContext.get(UserActivityService);

        autostart = URLParamsUtils.getAutostartEnabled();
    } else {
        console.info('Presentation bootstrap running in publication server mode.');
        applicationContext = getContext<DependencyContainer>(applicationContextKey);
    }

    const presentationContext = applicationContext.get(PresentationContext);
    presentationContext.isEmbedded = isEmbedded;
    const {
        presentationLoadedObservable,
        instanceErrorObservable,
        passwordNeededObservable,
        showPresentationLogsObservable,
        isOccupiedObservable
    } = presentationContext;
    const presentationBootstrapService = applicationContext.get(PresentationBootstrapService)!;

    async function tryOpenWithPassword(password: Nullable<string>) {
        await presentationBootstrapService.startBootstrapProcessAsync(password?.trim());
    }

    onMount(async () => {
        if (autostart)
            await presentationBootstrapService.startBootstrapProcessAsync();
    });

    const scenarioId = presentationBootstrapService.getScenarioId();

    const search = window.location.search;

    let startButtonEnabled = isEmbedded && !autostart;

    function startPresentation() {
        presentationBootstrapService.startBootstrapProcessAsync();
        startButtonEnabled = false;
    }

    let presentationIframe: HTMLIFrameElement | undefined;
    $: $presentationLoadedObservable && presentationIframe && userActivityService?.start([window, presentationIframe.contentWindow]);
    $: $presentationLoadedObservable && scenarioId !== undefined && presentationBootstrapService.listenToAuthorizationRequests(scenarioId);
</script>

{#if $instanceErrorObservable}
    <ErrorPage message={$instanceErrorObservable} retryButton={isEmbedded} stopButton={$isOccupiedObservable}/>
{:else if $presentationLoadedObservable}
    {#if $showPresentationLogsObservable}
        <PresentationLogViewer/>
    {/if}
    <iframe
            src={`/instance/${scenarioId}/${search}`}
            title="Presentation"
            bind:this={presentationIframe}
    />
{:else}
    <LoadingPage text={translations.pages.containerLoading.startingNewInstance}/>
{/if}

{#if $passwordNeededObservable}
    <PasswordPromptDialog scenarioId={scenarioId} callback={tryOpenWithPassword}/>
{/if}

{#if startButtonEnabled}
    <EmbeddedPresentationStarter action={startPresentation} {scenarioId}/>
{/if}

{#if import.meta.env.VITE_DISABLE_INTERNAL_BANNER !== 'true'}
    <CornerBanner color={Style.colors.secondary}>{translations.global.banner.internalVersion}</CornerBanner>
{/if}

<style>
    iframe {
        width: 100vw;
        height: 100%;
        overflow: clip;
    }
</style>
